<template>
  <protected-pages-layout>
    <div v-if="!pageReady" class="d-flex justify-content-center align-items-center m-5">
      <div class="spinner-border text-dark" style="width: 7.5rem; height: 7.5rem" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div v-else class="wrapper">
      <v-card rounded="0" class="main-content" flat>
        <v-card-actions class="card-actions">
          <div class="d-flex justify-center align-center">
            <v-btn icon="mdi-chevron-left" variant="plain" @click="prev"></v-btn>
            <v-item-group v-model="onboarding" class="text-center" mandatory>
              <v-item v-for="n in 3" :key="`btn-${n}`" v-slot="{ isSelected, toggle }" :value="n">
                <v-btn :variant="isSelected ? 'outlined' : 'text'" icon="mdi-record" @click="toggle"></v-btn>
              </v-item>
            </v-item-group>
            <v-btn icon="mdi-chevron-right" variant="plain" @click="next"></v-btn>
          </div>
        </v-card-actions>
        <v-window class="card-content-container" v-model="onboarding">
          <v-window-item class="chart-area" key="card-1" :value="1">
            <clientReport />
          </v-window-item>
          <v-window-item class="step-content" key="card-2" :value="2">
            <contractProfitabilityReport :sfClientId="currClient.SFAccountId" :clientId="clientId" />
          </v-window-item>
          <v-window-item class="chart-area" key="card-3" :value="3">
            <contractProfitabilityLineChart :lineChartData="lineChartData" />
          </v-window-item>
        </v-window>
      </v-card>
    </div>
  </protected-pages-layout>
</template>

<script setup>
import { periods, clientReportTypes, breakdowns } from "../assets/variables.js";
import { storeToRefs } from "pinia";
import { ref, onMounted } from "vue";
import { useClientReportStore } from "../stores/clientReport.js";
import { useClientStore } from "../stores/client.js";
import { router } from "../router/index.js";
import dayjs from "dayjs";

const clientReportStore = useClientReportStore();
const clientStore = useClientStore();

const { getReport } = clientReportStore;

const { fetchContractEco, fetchSfdcClientId } = clientStore;

const { getClient: currClient, contractEcoOverrun } = storeToRefs(clientStore);

const clientId = router.currentRoute.value.params.id;
let pageReady = ref(false);

let labels = [];
let CSOverrun = [];
let PSOverrun = [];
let MUVResourcesUsageOverrun = [];
let ECOAnnualizedHostingCostOverrun = [];
let ECOTotal = [];
let ECOTotalAnnualised = [];

let lineChartData = ref({
  labels: [],
  datasets: [],
});

let isLoading = ref(true);

onMounted(async () => {
  isLoading.value = true;
  await getReport();
  const sfClientId = await fetchSfdcClientId(clientId);
  await fetchContractEco(sfClientId, clientId);
  contractEcoOverrun.value.forEach((data) => {
    labels.push(dayjs(data.ReportDate).format("MMM-DD-YYYY"));
    CSOverrun.push(data.ECOCSMTime);
    PSOverrun.push(data.ECOPSTime);
    MUVResourcesUsageOverrun.push(data.ECOMUVResourceUsage);
    ECOAnnualizedHostingCostOverrun.push(data.ECOAnnualisedHostingCost);
    ECOTotal.push(data.ECOTotal);
    ECOTotalAnnualised.push(data.TotalAnnualisedCostOverrun);
  });
  console.log("labels: ", labels);
  labels = labels.sort((a, b) => new Date(a) - new Date(b));
  lineChartData.value = {
    labels: labels,
    datasets: [
      {
        label: "CS Overrun",
        data: CSOverrun,
        borderColor: "#3e95cd",
        fill: false,
        tension: 0.2,
      },
      {
        label: "PS Overrun",
        data: PSOverrun,
        borderColor: "#8e5ea2",
        fill: false,
        tension: 0.2,
      },
      {
        label: "MUV Resources Usage Overrun",
        data: ECOAnnualizedHostingCostOverrun,
        borderColor: "#3cba9f",
        fill: false,
        tension: 0.2,
      },
      {
        label: "ECO Total Annualised",
        data: ECOTotalAnnualised,
        borderColor: "#c45850",
        fill: false,
        tension: 0.2,
      },
    ],
  };
  isLoading.value = false;
  pageReady.value = true;
});
</script>

<script>
import { defineComponent } from "vue";
import Navbar from "../components/Navbar.vue";
import barChart from "../components/charts/TeamReportChart.vue";
import clientReport from "../components/clientReportsBarChart.vue";
import contractProfitabilityReport from "../components/clientReportContractProfitability.vue";
import contractProfitabilityLineChart from "../components/clientReportContractProfitabilityLineChart.vue";
import { clientReportTypes, periods, breakdowns } from "../assets/variables.js";
export default defineComponent({
  components: {
    navbar: Navbar,
    barChart,
    clientReport,
    contractProfitabilityReport,
    contractProfitabilityLineChart,
  },
  data: () => ({
    length: 3,
    onboarding: 1,
  }),
  methods: {
    next() {
      this.onboarding =
        this.onboarding + 1 > this.length ? 1 : this.onboarding + 1;
    },
    prev() {
      this.onboarding =
        this.onboarding - 1 < 1 ? this.length : this.onboarding - 1;
    },
  },
});
</script>

<style scoped lang="scss">
.wrapper {
  width: 100vw;
  height: 100vh;
  margin: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.main-content {
  flex: 1 1 auto !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 0 !important;
  background-color: #EDEDF3 !important;
  overflow: hidden !important;
  min-height: 0 !important;
}

.main-content :deep(.v-window__container) {
  flex: 1 1 auto !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 0 !important;
  background-color: #EDEDF3 !important;
  overflow: hidden !important;
  min-height: 0 !important;
}

.card-content-container {
  height: calc(100vh - 60px);
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.chart-area {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  max-height: 100%;
  height: 100%;
  padding: clamp(17px, 2vw, 44px); // Keep padding for charts
  box-sizing: border-box;
}
.step-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; // Change from center to flex-start
  align-items: stretch; // Change from center to stretch
  min-height: 0;
  overflow: auto;
  max-height: 80%;
  max-width: 100%;
  height: 100%;
  box-sizing: border-box;
}

:deep(.v-window-item.chart-area) {
  max-height: 100%;
  height: 100%;
  overflow: auto !important; // Allow both scrolling directions
}

:deep(.v-window-item.step-content) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow: auto !important;
  display: flex;
  flex-direction: column;
  padding: 0 !important; // Ensure no padding constraints
}

.card-actions {
  flex: 0 0 auto;
  background: #EDEDF3;
  border-bottom: 1px solid #eee;
  z-index: 2;
  padding: 12px 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
